import { NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  input,
  model,
  Output,
  signal,
  DOCUMENT
} from '@angular/core';

import { MatTabChangeEvent } from '@angular/material/tabs';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { TabsComponent } from '@uigy/shared/components/tabs/tabs.component';
import { Tab, TabConfig } from '@uigy/shared/components/tabs/tabs.interface';
import { MenuItem } from '@uigy/shared/models/interfaces';
import { LanguageService } from '@uigy/shared/services/language.service';
import {
  ActionButtonDefinition,
  ActionButtonVariant,
} from '@uigy/shared/components/ui-buttons-action-bar/models';
import {
  UiButtonsActionBarComponent,
} from '@uigy/shared/components/ui-buttons-action-bar/ui-buttons-action-bar.component';
import { DEFAULT_PADDING, drawerDefaultWidth, DRAWER_MIN_WIDTH } from '@uigy/shared/components/ui-drawer/constants';
import { DrawerWidthService } from '@uigy/shared/services/drawer-width.service';
import {
  NoResultOverlayComponent,
} from '@uigy/shared/components/ui-grid/components/overlays/no-result-overlay/no-result-overlay.component';
import { NoResultsFoundOverlay } from '@uigy/shared/components/ui-grid/models/overlays';
import {
  UiResizeHandleComponent,
} from '@uigy/shared/components/ui-resize-handle/ui-resize-handle.component';

@Component({
  selector: 'app-ui-drawer',

  templateUrl: './ui-drawer.component.html',
  styleUrls: ['./ui-drawer.component.scss'],
  imports: [NgStyle, UiButtonsActionBarComponent, UiResizeHandleComponent, NgClass, NoResultOverlayComponent, TabsComponent],
  providers: [DrawerWidthService],
})
export class UiDrawerComponent implements AfterViewInit {
  private el = inject(ElementRef<HTMLElement>);
  private doc = inject(DOCUMENT);
  private destroyRef = inject(DestroyRef);
  private languageService = inject(LanguageService);
  private drawerWidthService = inject(DrawerWidthService);
  private readonly DEFAULT_WIDTH: number = drawerDefaultWidth();
  private readonly MIN_WIDTH: number = DRAWER_MIN_WIDTH;

  public width = signal<number>(this.DEFAULT_WIDTH);
  public isOpen = model<boolean>(false);
  public menuItems = input<MenuItem[]>([]);
  public actionButtonAsIcon = input<boolean>();
  public isLoading = input<boolean>(false);
  public isEmpty = input<boolean>(true);
  public tabs = input<Tab[]>();
  public tabConfig = input<TabConfig>();
  public noResultsOverlay = input<NoResultsFoundOverlay | undefined>();
  public noResultsOverlayDisplay = computed(() => {
    const overlay = this.noResultsOverlay();
    if (!overlay) {
      return {
        title: this.languageService.texts.side_drawer.no_results.title,
        items: [{ text: this.languageService.texts.side_drawer.no_results.subtitle }],
      };
    }

    return overlay;
  });

  @HostBinding('style.top.px') private top: number = 0;

  @Output() closed = new EventEmitter<void>();
  @Output() tabChanged = new EventEmitter<MatTabChangeEvent>();
  @Output() widthChanged = new EventEmitter<number>();

  private resizing = false;
  private startX = 0;
  private startWidth = 0;

  constructor() {
    // Emit width changes via output and update service
    // This allows child components (like key-value grids) to react to drawer width changes
    effect(() => {
      const currentWidth = this.width();
      this.drawerWidthService.drawerWidth.set(currentWidth);
      this.widthChanged.emit(currentWidth);
    });

    effect(() => {
      if (this.isOpen()) {
        queueMicrotask(() => this.updateTopPosition());
      }
    });
  }

  ngAfterViewInit(): void {
    const scrollContainer = this.doc.querySelector('.main-layout .content-wrapper') as HTMLElement | null;
    if (scrollContainer) {
      const onScroll = (): void => this.updateTopPosition();
      scrollContainer.addEventListener('scroll', onScroll, { passive: true });
      this.destroyRef.onDestroy(() => scrollContainer.removeEventListener('scroll', onScroll));
    }
    this.updateTopPosition();
  }

  @HostListener('window:resize')
  private onResize(): void {
    this.updateTopPosition();
  }

  private updateTopPosition(): void {
    const contentEl = this.doc.querySelector('.main-layout .content') as HTMLElement | null;
    if (!contentEl) {
      return;
    }
    const rect = contentEl.getBoundingClientRect();
    this.top = Math.max(rect.top - 1, 0);
  }


  private get parentWidth(): number {
    const contentEl = this.doc.querySelector('.main-layout .content') as HTMLElement | null;
    const contentWidth = contentEl?.clientWidth ?? 0;
    const windowWidth = window.innerWidth;
    return contentWidth || windowWidth;
  }

  public startResize(event: MouseEvent): void {
    this.resizing = true;
    this.startX = event.clientX;
    this.startWidth = this.width();
    event.preventDefault();
    event.stopPropagation();
  }

  private close(): void {
    this.isOpen.set(false);
    this.closed.emit();
  }

  private toggleFullWidth(): void {
    const viewportW = this.parentWidth - DEFAULT_PADDING;
    const isFull = this.width() >= viewportW - 1;
    const newWidth = isFull ? this.DEFAULT_WIDTH : viewportW;
    this.width.set(newWidth);
  }


  private readonly isFull = computed<boolean>(() =>
    this.width() >= this.parentWidth - DEFAULT_PADDING - 1,
  );

  public readonly actionButtons = computed<ActionButtonDefinition[]>(() => {
    const baseActions: ActionButtonDefinition[] = [
      {
        variant: ActionButtonVariant.icon,
        icon: this.isFull() ? IconsEnum.minimize : IconsEnum.enlarge,
        action: () => this.toggleFullWidth(),
        isResponsive: true,
      },
      {
        variant: ActionButtonVariant.icon,
        icon: IconsEnum.close_large,
        action: () => this.close(),
        isResponsive: true,
      },
    ];

    if (this.menuItems().length) {
      if (this.actionButtonAsIcon()) {
        return [
          {
            variant: ActionButtonVariant.menu,
            icon: IconsEnum.more_vertical,
            items: [...this.menuItems()],
            isResponsive: true,
          },
          ...baseActions,
        ];
      } else {
        return [
          {
            variant: ActionButtonVariant.menu,
            label: this.languageService.texts.ui_grid.actions.label,
            items: [...this.menuItems()],
            isResponsive: true,
          },
          ...baseActions,
        ];
      }

    }

    return baseActions;
  });

  // The following method is used to set the width of the drawer when dragging the handle to resize
  @HostListener('document:mousemove', ['$event'])
  private onMouseMove(event: MouseEvent): void {
    if (!this.resizing) return;
    const delta = this.startX - event.clientX;
    const newWidth = Math.min(
      Math.max(this.startWidth + delta, this.MIN_WIDTH),
      this.parentWidth,
    );
    this.width.set(newWidth);
  }

  // The following method is used to stop setting the width of the drawer when stop dragging the handle to resize
  @HostListener('document:mouseup')
  private stopResize(): void {
    this.resizing = false;
  }

  public onTabChanged(event: MatTabChangeEvent): void {
    this.tabChanged.emit(event);
  }
}
